import { formatMoney, prototypePercent, prototypeSpread, type ProjectFunnelDto } from '@nsb/core';
import { dateWords } from '../days.ts';
import { aheadEnd } from './funnelKey.ts';

/**
 * The sentence over the funnel (#128; Jason, 2026-10-29: "This should read on any project something like 'projected
 * final cost is $xxx, the average of the spread of the most recent consolidated estimates'"; design study 31, A): the
 * projected final cost and what it comes from, made from the facts alone, never a figure nobody set. It is where the
 * drawing's lines meet (`aheadEnd`: the confirmed budget for the work, L-8, else the middle of the latest estimate), so
 * the sentence and the drawing never disagree; with a budget, what is held on top of it and what the latest estimate runs
 * over it. An ended job projects nothing. The phases' facts are each beside its name on the funnel (#129). The same facts
 * for NSB and the client, in the reader's words.
 */
export function funnelSentence(f: ProjectFunnelDto, o: { client: boolean }): string {
  const money = (low: number, high: number) => (low === high ? formatMoney(low) : `${formatMoney(low)} to ${formatMoney(high)}`);
  const last = f.estimates.at(-1);
  if (f.endedOn) return [`The project ended ${dateWords(f.endedOn, 'date')}.`, ...(last ? [`Its last estimate was ${money(last.lowCents, last.highCents)}.`] : [])].join(' ');
  if (!last) {
    const first = f.gates.find((g) => g.deliversEstimate && !g.estimate);
    return ['No estimate yet.', ...(first ? [`The first estimate comes with ${first.name}.`] : [])].join(' ');
  }
  if (f.cap) {
    const held = f.cap.totalCents - f.cap.workCents;
    const parts = [`Projected final cost is ${formatMoney(f.cap.workCents)}: ${o.client ? 'your' : 'the'} confirmed budget for the work, with ${formatMoney(held)} held on top.`];
    if (last.lowCents > f.cap.totalCents)
      parts.push(`The latest estimate, ${money(last.lowCents, last.highCents)}, runs over ${formatMoney(f.cap.totalCents)} all in by at least ${formatMoney(last.lowCents - f.cap.totalCents)}.`);
    else if (last.highCents > f.cap.totalCents)
      parts.push(`The latest estimate’s top, ${formatMoney(last.highCents)}, runs ${formatMoney(last.highCents - f.cap.totalCents)} over ${formatMoney(f.cap.totalCents)} all in.`);
    return parts.join(' ');
  }
  // Where the lines meet ahead, else (an estimate set after today, nothing drawn ahead of it yet) the same middle.
  const end = aheadEnd(f)?.cents ?? Math.round((last.lowCents + last.highCents) / 2);
  if (last.lowCents === last.highCents) return `Projected final cost is about ${formatMoney(end)}: the latest estimate.`;
  return `Projected final cost is about ${formatMoney(end)}: the middle of the latest estimate, ${money(last.lowCents, last.highCents)}.`;
}

/**
 * The sentence over the master's prototypical job (L-12): the phases' shares of a job's time, as the funnel lays them
 * out, and how sure the price is at the start and by the end; a phase with no share takes no time, and says so. Never
 * weeks, dates or money: the master has none.
 */
export function prototypeSentence(f: ProjectFunnelDto): string {
  if (!f.band.length) return 'No phase has a share of the time yet: set each phase’s share on its card and the funnel lays the phases out.';
  const share = (p: ProjectFunnelDto['phases'][number]) => (p.startOn && p.endOn ? prototypePercent(f, p.endOn) - prototypePercent(f, p.startOn) : 0);
  const timed = f.phases.filter((p) => share(p) > 0);
  const none = f.phases.filter((p) => share(p) <= 0).map((p) => p.name);
  const spread = (r: { highCents: number }) => `±${Math.round(prototypeSpread(r) * 100)}%`;
  const lastTimed = [...f.phases].reverse().find((p) => share(p) > 0 && p.range);
  const parts = [`A job’s time, by the phases’ shares: ${timed.map((p) => `${p.name} ${Math.round(share(p))}%`).join(', ')}.`];
  if (none.length) parts.push(`${none.join(', ')} ${none.length === 1 ? 'has' : 'have'} no share of the time yet, so ${none.length === 1 ? 'it takes' : 'they take'} none.`);
  parts.push(`The price is ${spread(f.band[0]!)} at the start${lastTimed ? ` and ${spread(lastTimed.range!)} by the end of ${lastTimed.name}` : ''}.`);
  return parts.join(' ');
}
